/* eslint-disable */
// useDelayReportExport — builds + downloads the Delay Report .xlsx.
// Same SheetJS pattern as useBackupExport (dynamic import('xlsx'), writeFile).
//
// =IMAGE() cell shape: json_to_sheet writes literal values only and cannot
// carry a formula, so each data sheet is built with aoa_to_sheet (header +
// rows) and the Image column cells are then patched to formula cells:
//   ws[ref] = { t: 's', f: 'IMAGE("<url>")' }
// SheetJS prefixes the '=' on write; the cell recalculates in Excel 365 /
// Google Sheets to render the photo inline.

import { useState, useCallback } from 'react';
import { sanitizeSheetName, formatDMY, todayStamp, groupsFromRows } from './delayReportUtils';

function buildColumns(includeImage) {
  const cols = [];
  if (includeImage) cols.push({ header: 'Image', image: true, get: () => '' });
  cols.push(
    { header: 'Dev #',         get: r => r.devNumber },
    { header: 'Model',         get: r => r.model },
    { header: 'Designer',      get: r => r.designer },
    { header: 'Factory',       get: r => r.factoryFull || r.factoryCode },
    { header: 'Stage',         get: r => r.stage },
    { header: 'Status',        get: r => r.status },
    { header: 'Side',          get: r => (r.side === 'factory' ? 'Factory' : 'Designer') },
    { header: 'Days waiting',  get: r => r.daysWaiting },
    { header: 'Threshold',     get: r => r.threshold },
    { header: 'Days over',     get: r => r.daysOver },
    { header: 'Status set at', get: r => formatDMY(r.statusSetAt) },
  );
  return cols;
}

function buildDataSheet(XLSX, rows, cols, includeImage) {
  const header = cols.map(c => c.header);
  const aoa = [header, ...rows.map(r => cols.map(c => c.get(r)))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  if (includeImage) {
    const imgCol = cols.findIndex(c => c.image);
    if (imgCol >= 0) {
      rows.forEach((r, i) => {
        if (!r.imageUrl) return;
        const ref = XLSX.utils.encode_cell({ r: i + 1, c: imgCol }); // +1 skips header
        ws[ref] = { t: 's', f: `IMAGE("${r.imageUrl}")` };
      });
    }
  }

  // Widths + row heights so embedded images are visible.
  ws['!cols'] = cols.map(c => (c.image ? { wpx: 88 } : { wch: c.header === 'Status' ? 26 : 14 }));
  if (includeImage) ws['!rows'] = [{ hpt: 16 }, ...rows.map(() => ({ hpt: 44 }))];
  return ws;
}

function buildSummarySheet(XLSX, rows) {
  const groups = groupsFromRows(rows);
  const header = ['Group', 'Drawing', 'Hand Made', 'Color', 'Total'];
  const aoa = [header];
  for (const g of groups) {
    const gr = rows.filter(r => r.groupKey === g.key);
    const draw  = gr.filter(r => r.stageKey === 'drawing').length;
    const hand  = gr.filter(r => r.stageKey === 'hand').length;
    const color = gr.filter(r => r.stageKey === 'color').length;
    aoa.push([g.label, draw, hand, color, gr.length]);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 }];
  return ws;
}

export function useDelayReportExport() {
  const [exporting, setExporting] = useState(false);

  const run = useCallback(async ({ rows, mode, options }) => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const { includeImage, summarySheet, oneSheetPerGroup } = options;
      const cols = buildColumns(includeImage);
      const wb = XLSX.utils.book_new();
      // Force Excel/Sheets to recompute formulas on open so =IMAGE() cells
      // render immediately (without this, they stay blank/#NAME? until a
      // per-cell double-click + Enter forces a recalc). Cell shape stays
      // { t:'s', f:'IMAGE("url")' } with no cached v.
      wb.Workbook = { CalcPr: { fullCalcOnLoad: true } };

      if (summarySheet) {
        XLSX.utils.book_append_sheet(wb, buildSummarySheet(XLSX, rows), 'Summary');
      }

      if (oneSheetPerGroup) {
        const groups = groupsFromRows(rows);
        const usedNames = new Set();
        for (const g of groups) {
          const gr = rows
            .filter(r => r.groupKey === g.key)
            .sort((a, b) => b.daysOver - a.daysOver);
          let name = sanitizeSheetName(g.label, 'Group');
          // Dedupe sheet names (Excel rejects duplicates), keep ≤31 chars.
          let n = name, i = 2;
          while (usedNames.has(n.toLowerCase())) { n = sanitizeSheetName(`${name} ${i++}`); }
          usedNames.add(n.toLowerCase());
          XLSX.utils.book_append_sheet(wb, buildDataSheet(XLSX, gr, cols, includeImage), n);
        }
        // Defensive: a workbook must have at least one sheet.
        if (!wb.SheetNames.length) {
          XLSX.utils.book_append_sheet(wb, buildDataSheet(XLSX, [], cols, includeImage), 'Delayed');
        }
      } else {
        const sorted = [...rows].sort((a, b) => {
          const g = String(a.groupLabel).toLowerCase().localeCompare(String(b.groupLabel).toLowerCase());
          return g !== 0 ? g : b.daysOver - a.daysOver;
        });
        XLSX.utils.book_append_sheet(wb, buildDataSheet(XLSX, sorted, cols, includeImage), 'Delayed');
      }

      const modeSlug = mode === 'des' ? 'by-designer' : 'by-factory';
      XLSX.writeFile(wb, `delay-report_${modeSlug}_${todayStamp()}.xlsx`);
    } finally {
      setExporting(false);
    }
  }, []);

  return { run, exporting };
}
